'use client';

import { ReactNode } from 'react';
import { useField } from 'formik';
import {
  Box,
  Checkbox,
  FormGroup,
  InputLabel,
  FormControl,
  FormGroupProps,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';

import { controlError } from 'utils/error';
import { QuestionLabel } from 'components/question-label';

export interface CheckboxFieldProps extends Omit<
  FormGroupProps,
  'children' | 'onChange'
> {
  name: string;
  label?: string;
  required?: boolean;
  children?: ReactNode;
  displayValue?: string[];
  mapValue?: (values: string[]) => unknown;
  options: { label: string; value: string }[];
}

export const CheckboxField = ({
  name,
  label,
  options,
  required,
  children,
  mapValue,
  displayValue,
  ...props
}: CheckboxFieldProps) => {
  const [field, meta, helpers] = useField<unknown>(name);
  const selectedValues =
    displayValue ?? (Array.isArray(field.value) ? field.value : []);
  const errorText = controlError(meta, name, label);

  return (
    <Box>
      {label && (
        <InputLabel component="div" className="mb-2" required={required}>
          <QuestionLabel value={label} />
        </InputLabel>
      )}
      <FormControl fullWidth required={required} error={Boolean(errorText)}>
        <FormGroup {...props}>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              label={option.label}
              control={
                <Checkbox
                  name={name}
                  onBlur={() => helpers.setTouched(true, true)}
                  checked={selectedValues.includes(option.value)}
                  onChange={(_, checked) => {
                    const nextValue = checked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter(
                          (value) => value !== option.value
                        );
                    helpers.setValue(
                      mapValue ? mapValue(nextValue) : nextValue,
                      true
                    );
                  }}
                />
              }
            />
          ))}
        </FormGroup>
        {children}
        {errorText && <FormHelperText>{errorText}</FormHelperText>}
      </FormControl>
    </Box>
  );
};
